/**
 * Recent stage jobs — admin observability view (Sub-Chunk 7 of
 * the claims-async migration).
 *
 * Two-pane layout: a filterable table on the left, a per-row
 * drill-down drawer on the right. Filters mirror DJ's QA
 * workflow:
 *   - status (multi-select)
 *   - "fallback only" toggle (rows with fallback_count > 0)
 *   - matter ID search
 *
 * The drill-down shows step_history (durable snapshot copied at
 * terminal time), the live Redis progress beacon if the RQ
 * window hasn't elapsed, and the linked llm_call_log rows for
 * the same matter+stage. A re-run button is available on
 * terminal states (terminal jobs only — RQ refuses duplicate
 * enqueue for in-flight ones).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, RefreshCw, AlertCircle } from 'lucide-react';
import * as api from '@/api/client';

interface StageJobRow {
  id: string;
  rq_job_id: string;
  matter_id: string;
  module: string;
  stage: string;
  action: string;
  status: string;
  final_provider: string | null;
  fallback_count: number;
  error_class: string | null;
  error_message: string | null;
  result_summary: any;
  params_summary: any;
  step_history: any;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

interface DrilldownPayload {
  row: StageJobRow;
  live_progress: any;
  llm_calls: Array<{
    prompt_key: string | null;
    stage: string | null;
    model: string;
    parse_status: string;
    duration_ms: number | null;
    response_tokens_estimate: number | null;
    fallback_from: string | null;
    created_at: string;
  }>;
}

const STATUS_FILTERS = [
  'queued', 'running', 'succeeded', 'failed',
  'refused_all_providers', 'cancelled',
];

const TERMINAL_STATUSES = new Set([
  'succeeded', 'failed', 'refused_all_providers', 'cancelled',
]);

function StatusChip({ status }: { status: string }) {
  const palette: Record<string, string> = {
    queued:                'bg-zinc-100 text-zinc-700 border-zinc-300',
    running:               'bg-blue-50 text-blue-800 border-blue-300',
    succeeded:             'bg-green-50 text-green-800 border-green-300',
    failed:                'bg-amber-50 text-amber-800 border-amber-300',
    refused_all_providers: 'bg-red-50 text-red-800 border-red-300',
    cancelled:             'bg-zinc-100 text-zinc-600 border-zinc-300',
  };
  const cls = palette[status] || 'bg-zinc-100 text-zinc-700 border-zinc-300';
  return (
    <span className={`px-1.5 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-wide ${cls}`}>
      {status}
    </span>
  );
}

function fmtTs(ts: string | null | undefined): string {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

export default function StageJobsAdmin({ onBack }: { onBack: () => void }) {
  const [rows, setRows] = useState<StageJobRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [matterFilter, setMatterFilter] = useState<string>('');
  const [fallbackOnly, setFallbackOnly] = useState(false);

  // Drill-down
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [drilldown, setDrilldown] = useState<DrilldownPayload | null>(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [drilldownError, setDrilldownError] = useState<string | null>(null);
  const [rerunPending, setRerunPending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.adminListStageJobs({
        status: statusFilter || undefined,
        matter_id: matterFilter.trim() || undefined,
        fallback_only: fallbackOnly,
        limit: 200,
      });
      setRows(data.rows || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, matterFilter, fallbackOnly]);

  useEffect(() => {
    load();
  }, [load]);

  const loadDrilldown = useCallback(async (id: string) => {
    setSelectedId(id);
    setDrilldown(null);
    setDrilldownLoading(true);
    setDrilldownError(null);
    try {
      const data = await api.adminStageJobDrilldown(id);
      setDrilldown(data);
    } catch (e: any) {
      setDrilldownError(
        e?.response?.data?.detail || e?.message || 'Drill-down failed',
      );
    } finally {
      setDrilldownLoading(false);
    }
  }, []);

  const handleRerun = useCallback(async () => {
    if (!drilldown) return;
    if (!window.confirm(
      `Re-run ${drilldown.row.stage}:${drilldown.row.action} for matter ${drilldown.row.matter_id}?`,
    )) return;
    setRerunPending(true);
    try {
      await api.adminRerunStageJob(drilldown.row.id);
      // Re-load both the list and the drill-down — the row id
      // changes on rerun (we DELETE the old + INSERT a new
      // history row keyed to the same rq_job_id), so the
      // selected-id will likely 404 on next refresh; clear it.
      setSelectedId(null);
      setDrilldown(null);
      await load();
    } catch (e: any) {
      setDrilldownError(
        e?.response?.data?.detail || e?.message || 'Re-run failed',
      );
    } finally {
      setRerunPending(false);
    }
  }, [drilldown, load]);

  const isTerminalSelected = drilldown
    ? TERMINAL_STATUSES.has(drilldown.row.status)
    : false;

  const fallbackRowCount = useMemo(
    () => rows.filter(r => (r.fallback_count || 0) > 0).length,
    [rows],
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="text-sm text-zinc-500 hover:text-zinc-800 inline-flex items-center gap-1"
          >
            <ArrowLeft size={14} /> Back
          </button>
          <h1 className="text-lg font-semibold text-zinc-900">
            Recent stage jobs
          </h1>
          <span className="text-xs text-zinc-500">
            {rows.length} rows · {fallbackRowCount} via fallback
          </span>
        </div>
        <button
          type="button"
          onClick={load}
          className="text-xs px-2 py-1 border border-zinc-300 rounded-sm hover:bg-zinc-50 inline-flex items-center gap-1"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4 p-3 border border-zinc-200 rounded-sm bg-zinc-50">
        <label className="text-xs flex items-center gap-1.5">
          Status
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="stage-jobs-status-filter"
            className="ml-1 text-xs border border-zinc-300 rounded-sm px-2 py-1 bg-white"
          >
            <option value="">all</option>
            {STATUS_FILTERS.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
        <label className="text-xs flex items-center gap-1.5">
          Matter ID
          <input
            type="text"
            value={matterFilter}
            onChange={(e) => setMatterFilter(e.target.value)}
            placeholder="exact uuid…"
            data-testid="stage-jobs-matter-filter"
            className="ml-1 text-xs border border-zinc-300 rounded-sm px-2 py-1 bg-white w-72 font-mono"
          />
        </label>
        <label className="text-xs flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={fallbackOnly}
            onChange={(e) => setFallbackOnly(e.target.checked)}
            data-testid="stage-jobs-fallback-only"
          />
          Fallback only (chain_position &gt; 0)
        </label>
        {error && (
          <span className="text-xs text-red-700 inline-flex items-center gap-1">
            <AlertCircle size={12} /> {error}
          </span>
        )}
      </div>

      <div className="grid grid-cols-[2fr_3fr] gap-4 items-start">
        {/* Table */}
        <div className="border border-zinc-200 rounded-sm bg-white overflow-hidden">
          <table className="w-full text-xs" data-testid="stage-jobs-table">
            <thead className="bg-zinc-100 text-zinc-600">
              <tr>
                <th className="text-left px-2 py-1.5">Stage / Action</th>
                <th className="text-left px-2 py-1.5">Status</th>
                <th className="text-left px-2 py-1.5">Provider</th>
                <th className="text-left px-2 py-1.5">FB</th>
                <th className="text-left px-2 py-1.5">Created</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="px-2 py-3 text-zinc-500 italic">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={5} className="px-2 py-3 text-zinc-500 italic">No rows.</td></tr>
              )}
              {rows.map(row => (
                <tr
                  key={row.id}
                  onClick={() => loadDrilldown(row.id)}
                  data-testid={`stage-job-row-${row.id}`}
                  className={
                    'cursor-pointer hover:bg-blue-50 border-t border-zinc-100 ' +
                    (selectedId === row.id ? 'bg-blue-100' : '')
                  }
                >
                  <td className="px-2 py-1.5">
                    <div className="font-mono text-[11px] text-zinc-700">
                      {row.stage} · {row.action}
                    </div>
                    <div className="font-mono text-[10px] text-zinc-400 truncate" title={row.matter_id}>
                      {row.matter_id.slice(0, 12)}…
                    </div>
                  </td>
                  <td className="px-2 py-1.5"><StatusChip status={row.status} /></td>
                  <td className="px-2 py-1.5">
                    {row.final_provider ? (
                      <span className="font-mono text-[10px] text-zinc-700">
                        {row.final_provider}
                      </span>
                    ) : (
                      <span className="text-zinc-400">—</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    {(row.fallback_count || 0) > 0 ? (
                      <span className="px-1.5 py-0.5 rounded-sm bg-amber-100 border border-amber-300 text-[10px] text-amber-800">
                        {row.fallback_count}
                      </span>
                    ) : (
                      <span className="text-zinc-400">0</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-[10px] text-zinc-500">
                    {fmtTs(row.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Drill-down drawer */}
        <div className="border border-zinc-200 rounded-sm bg-white p-4 min-h-[320px]">
          {!selectedId && (
            <div className="text-xs text-zinc-500 italic">
              Click a row to inspect.
            </div>
          )}
          {selectedId && drilldownLoading && (
            <div className="text-xs text-zinc-500 italic">Loading drill-down…</div>
          )}
          {drilldownError && (
            <div className="text-xs text-red-700 inline-flex items-center gap-1">
              <AlertCircle size={12} /> {drilldownError}
            </div>
          )}
          {drilldown && (
            <div data-testid="stage-jobs-drilldown" className="space-y-4 text-xs">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <StatusChip status={drilldown.row.status} />
                  <span className="font-mono text-[11px]">
                    {drilldown.row.stage} · {drilldown.row.action}
                  </span>
                </div>
                <div className="text-[10px] text-zinc-500 font-mono break-all">
                  rq_job_id: {drilldown.row.rq_job_id}
                </div>
                <div className="text-[10px] text-zinc-500 font-mono break-all">
                  matter: {drilldown.row.matter_id}
                </div>
                <div className="text-[10px] text-zinc-500 mt-1">
                  enqueued {fmtTs(drilldown.row.created_at)} · started {fmtTs(drilldown.row.started_at)} · finished {fmtTs(drilldown.row.finished_at)}
                </div>
                {drilldown.row.final_provider && (
                  <div className="text-[10px] text-zinc-700 mt-1">
                    final provider: <span className="font-mono">{drilldown.row.final_provider}</span>
                    {(drilldown.row.fallback_count || 0) > 0 && (
                      <span className="ml-2 px-1.5 py-0.5 rounded-sm bg-amber-100 border border-amber-300">
                        fallback_count={drilldown.row.fallback_count}
                      </span>
                    )}
                  </div>
                )}
                {drilldown.row.error_class && (
                  <div className="mt-2 px-2 py-1.5 bg-red-50 border border-red-200 rounded-sm">
                    <div className="text-[10px] font-mono uppercase tracking-wide text-red-700">
                      {drilldown.row.error_class}
                    </div>
                    {drilldown.row.error_message && (
                      <pre className="mt-1 text-[10px] text-red-800 whitespace-pre-wrap font-mono">
                        {drilldown.row.error_message}
                      </pre>
                    )}
                  </div>
                )}
                {isTerminalSelected && (
                  <button
                    type="button"
                    onClick={handleRerun}
                    disabled={rerunPending}
                    data-testid="stage-jobs-rerun-button"
                    className="mt-3 text-xs px-3 py-1.5 bg-[#002FA7] text-white rounded-sm hover:bg-[#002277] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                  >
                    {rerunPending ? 'Re-running…' : 'Re-run this job'}
                  </button>
                )}
              </div>

              {/* Step history */}
              {Array.isArray(drilldown.row.step_history) && drilldown.row.step_history.length > 0 && (
                <div>
                  <div className="font-medium text-zinc-700 mb-1">Step history (terminal snapshot)</div>
                  <ul className="space-y-1">
                    {drilldown.row.step_history.map((s: any, i: number) => (
                      <li key={i} className="font-mono text-[10px] text-zinc-600 border-l-2 border-zinc-300 pl-2">
                        <span className="text-[#002FA7]">{s?.step || '?'}</span>
                        {' — '}
                        {s?.message || ''}
                        {s?.provider && (
                          <span className="ml-1 text-zinc-500">[{s.provider}]</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Live progress (if RQ still has it) */}
              {drilldown.live_progress && (
                <div>
                  <div className="font-medium text-zinc-700 mb-1">Live progress (Redis beacon)</div>
                  <pre className="font-mono text-[10px] text-zinc-700 bg-zinc-50 border border-zinc-200 rounded-sm p-2 whitespace-pre-wrap">
                    {JSON.stringify(drilldown.live_progress, null, 2)}
                  </pre>
                </div>
              )}

              {/* Linked LLM calls */}
              {drilldown.llm_calls && drilldown.llm_calls.length > 0 && (
                <div>
                  <div className="font-medium text-zinc-700 mb-1">
                    Recent LLM calls for this matter+stage ({drilldown.llm_calls.length})
                  </div>
                  <table className="w-full text-[10px]">
                    <thead className="text-zinc-500">
                      <tr>
                        <th className="text-left">prompt_key</th>
                        <th className="text-left">model</th>
                        <th className="text-left">parse</th>
                        <th className="text-right">ms</th>
                        <th className="text-left">fallback_from</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drilldown.llm_calls.map((c, i) => (
                        <tr key={i} className="border-t border-zinc-100">
                          <td className="font-mono text-zinc-700">{c.prompt_key || '—'}</td>
                          <td className="font-mono text-zinc-700">{c.model}</td>
                          <td>{c.parse_status}</td>
                          <td className="text-right">{c.duration_ms ?? '—'}</td>
                          <td className="font-mono text-zinc-500">{c.fallback_from || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Result + params summaries */}
              {drilldown.row.result_summary && (
                <div>
                  <div className="font-medium text-zinc-700 mb-1">Result summary</div>
                  <pre className="font-mono text-[10px] text-zinc-700 bg-zinc-50 border border-zinc-200 rounded-sm p-2 whitespace-pre-wrap">
                    {JSON.stringify(drilldown.row.result_summary, null, 2)}
                  </pre>
                </div>
              )}
              {drilldown.row.params_summary && (
                <div>
                  <div className="font-medium text-zinc-700 mb-1">Params</div>
                  <pre className="font-mono text-[10px] text-zinc-700 bg-zinc-50 border border-zinc-200 rounded-sm p-2 whitespace-pre-wrap">
                    {JSON.stringify(drilldown.row.params_summary, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
